import { normalize, schema, denormalize } from 'normalizr';
import { isInRange } from '../../Utils/MathUtils';
import { MAX_RANGE, MIN_RANGE } from '../../Utils/Constants';
import { isArrayEqual } from '../../Utils/ArrayUtils';

/**
 * This method normalizes object containing list of sessions
 * It returns object containing 2 keys
 * 1st is byKey. This is an object of sessionKey and session
 * 2nd is allKeys. This is an array containing all sessionKeys
 *
 * @param entitiesMap
 * @returns {{byKey, allKeys}}
 */

const session = new schema.Entity('sessions', {}, { idAttribute: 'sessionKey' });
const sessionsSchema = { sessions: [session] };

export function normalizeSessions(sessionsList = []) {
  if (sessionsList.length === 0) {
    return {
      byKey: {},
      allKeys: []
    };
  }
  const { result, entities } = normalize({ sessions: sessionsList }, sessionsSchema);
  return {
    byKey: entities.sessions,
    allKeys: result.sessions
  };
}

/**
 * This method de normalizes sessionsMap for given sessionKeys
 *
 * @param sessionKeys
 * @param sessionsMap
 * @returns [sessions]
 */
export function denormalizeSessions(sessionKeys, sessionsMap = {}) {
  if (sessionKeys === null || sessionKeys.length === 0) {
    return [];
  }
  const deNormalizedData = denormalize({ sessions: sessionKeys }, sessionsSchema, { sessions: sessionsMap });
  return deNormalizedData.sessions;
}

export function computeSessionStats(byKey, sessionKeys = []) {
  return sessionKeys.reduce(
    (accumulator, currentValue) => {
      const session = byKey[currentValue] || {};
      const { registrantCount = 0, registrantsAttended = 0 } = session;
      const { sessionQAndACount = 0, attendeePollResponseCount = 0, surveyQuestionResponseCount = 0 } =
        session.sessionStatsInfo || {};
      const { totalRegistrants, totalAttendees, totalQAndACount, totalEngagementCount } = accumulator;

      return {
        totalRegistrants: totalRegistrants + registrantCount,
        totalAttendees: totalAttendees + registrantsAttended,
        totalQAndACount: totalQAndACount + sessionQAndACount,
        totalEngagementCount:
          totalEngagementCount + sessionQAndACount + attendeePollResponseCount + surveyQuestionResponseCount
      };
    },
    {
      totalRegistrants: 0,
      totalAttendees: 0,
      totalQAndACount: 0,
      totalEngagementCount: 0
    }
  );
}

export function getUpdatedStatsOnToggleSessionKey(existingStats, isSelected, session = {}) {
  let {
    filteredSelectedTotalRegistrants = 0,
    filteredSelectedTotalAttendees = 0,
    filteredSelectedTotalQAndACount = 0,
    filteredSelectedTotalEngagementCount = 0
  } = existingStats;

  const { registrantCount = 0, registrantsAttended = 0 } = session;
  const { sessionQAndACount = 0, attendeePollResponseCount = 0, surveyQuestionResponseCount = 0 } =
    session.sessionStatsInfo || {};

  if (isSelected) {
    filteredSelectedTotalRegistrants += registrantCount;
    filteredSelectedTotalAttendees += registrantsAttended;
    filteredSelectedTotalQAndACount += sessionQAndACount;
    filteredSelectedTotalEngagementCount += sessionQAndACount + attendeePollResponseCount + surveyQuestionResponseCount;
  } else {
    filteredSelectedTotalRegistrants -= registrantCount;
    filteredSelectedTotalAttendees -= registrantsAttended;
    filteredSelectedTotalQAndACount -= sessionQAndACount;
    filteredSelectedTotalEngagementCount -= sessionQAndACount + attendeePollResponseCount + surveyQuestionResponseCount;
  }

  return {
    filteredSelectedTotalRegistrants,
    filteredSelectedTotalAttendees,
    filteredSelectedTotalQAndACount,
    filteredSelectedTotalEngagementCount
  };
}

export function getUpdatedStatsOnToggleAllSessionKeys(existingStats, isSelected) {
  let result = {
    filteredSelectedTotalRegistrants: 0,
    filteredSelectedTotalAttendees: 0,
    filteredSelectedTotalQAndACount: 0,
    filteredSelectedTotalEngagementCount: 0
  };

  if (isSelected) {
    result.filteredSelectedTotalRegistrants = existingStats.filteredTotalRegistrants;
    result.filteredSelectedTotalAttendees = existingStats.filteredTotalAttendees;
    result.filteredSelectedTotalQAndACount = existingStats.filteredTotalQAndACount;
    result.filteredSelectedTotalEngagementCount = existingStats.filteredTotalEngagementCount;
  }

  return result;
}

export function getFilteredSessionKeys(sessionsMap, registrantRange, attendeeRange, searchString, allSessionKeys = []) {
  const defaultRange = [MIN_RANGE, MAX_RANGE];
  if (
    isArrayEqual(registrantRange, defaultRange) &&
    isArrayEqual(attendeeRange, defaultRange) &&
    searchString === null
  ) {
    return [...allSessionKeys];
  }

  return allSessionKeys.filter(sessionKey => {
    const session = sessionsMap[sessionKey] || {};
    const { registrantCount, registrantsAttended, webinarName } = session;
    return (
      isInRange(registrantCount, registrantRange) &&
      isInRange(registrantsAttended, attendeeRange) &&
      (searchString && webinarName ? webinarName.toUpperCase().includes(searchString.toUpperCase()) : true)
    );
  });
}

export function getCurrentSession(session) {
  if (session.uniqueTableId !== 'sessionsTableInGenerateReports') {
    return { currentSession: session.sessions, inGenerateReport: false };
  } else {
    return { currentSession: session.filterReportSessions, inGenerateReport: true };
  }
}



// WEBPACK FOOTER //
// ./src/Reducks/Sessions/SessionsUtils.js